import { createApiClient } from './client'

// Types
export interface AllergyAlert {
  userId: string
  userName: string
  conflictingItems: string[]
}

export interface MealNeed {
  id: string
  trip_id: string
  name: string
  description?: string
  meal_type: string
  planned_date: string
  planned_meal_time: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  serves_count?: number | null
  is_collaborative: boolean
  ingredients: string[]
  cooking_equipment: string[]
  dietary_restrictions: string[]
  allergens: string[]
  notes?: string
  created_by_user_id: string
  created_at: string
  updated_at: string
  contributors: MealContributor[]
  created_by_user: {
    id: string
    name?: string
    full_name?: string
    email?: string
    avatar_url?: string
    user?: {
      id?: string
      name?: string
      full_name?: string | null
      email?: string
      avatar_url?: string
    }
  }
  allergyAlerts?: AllergyAlert[]
  /** Number of trip members eating this meal */
  eating_count?: number
  /** Total trip members (for headcount display) */
  total_members?: number
  /** Whether the current user has opted out of this meal */
  current_user_opted_out?: boolean
  /** Number of trip members who opted out */
  opt_out_count?: number
}

export interface MealContributor {
  id: string
  meal_id: string
  member_id: string
  contribution_type: 'cooking' | 'prep' | 'bringing_ingredients' | 'cleanup'
  notes?: string
  added_at: string
  member: {
    id: string
    user_id: string
    military_role?: string
    user: {
      id: string
      name: string
      full_name?: string
      avatar_url?: string
    }
  }
}

export interface CreateMealNeedRequest {
  name: string
  description?: string
  meal_type?: string
  planned_date: string
  planned_meal_time: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  serves_count?: number
  is_collaborative?: boolean
  ingredients?: string[]
  cooking_equipment?: string[]
  dietary_restrictions?: string[]
  allergens?: string[]
  notes?: string
}

export interface UpdateMealNeedRequest {
  name?: string
  description?: string
  meal_type?: string
  planned_date?: string
  planned_meal_time?: 'breakfast' | 'lunch' | 'dinner' | 'snack'
  serves_count?: number
  is_collaborative?: boolean
  ingredients?: string[]
  cooking_equipment?: string[]
  dietary_restrictions?: string[]
  allergens?: string[]
  notes?: string
}

export interface CreateMealContributorRequest {
  contribution_type: 'cooking' | 'prep' | 'bringing_ingredients' | 'cleanup'
  notes?: string
}

export interface UpdateMealContributorRequest {
  contribution_type?: 'cooking' | 'prep' | 'bringing_ingredients' | 'cleanup'
  notes?: string
}

export class MealNeedsApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  private unwrapResponse<T>(response: any): T {
    // Backend returns { success: true, data: [...] } format
    if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
      return response.data as T
    }
    // Fallback: assume response is already the data
    return response as T
  }

  // Get all meals for a trip
  async getTripMeals(tripId: string): Promise<MealNeed[]> {
    const response = await this.apiClient.get(`/trips/${tripId}/meals`)
    return this.unwrapResponse<MealNeed[]>(response)
  }

  // Get single meal
  async getMealById(mealId: string): Promise<MealNeed> {
    const response = await this.apiClient.get(`/meals/${mealId}`)
    return this.unwrapResponse<MealNeed>(response)
  }

  // Create new meal
  async createMeal(tripId: string, mealData: CreateMealNeedRequest): Promise<MealNeed> {
    const response = await this.apiClient.post(`/trips/${tripId}/meals`, mealData)
    return this.unwrapResponse<MealNeed>(response)
  }

  // Update meal
  async updateMeal(mealId: string, updateData: UpdateMealNeedRequest): Promise<MealNeed> {
    const response = await this.apiClient.patch(`/meals/${mealId}`, updateData)
    return this.unwrapResponse<MealNeed>(response)
  }

  // Delete meal
  async deleteMeal(mealId: string): Promise<void> {
    await this.apiClient.delete(`/meals/${mealId}`)
  }

  // Add meal contributor
  async addMealContributor(mealId: string, contributorData: CreateMealContributorRequest): Promise<MealContributor> {
    const response = await this.apiClient.post(`/meals/${mealId}/contributors`, contributorData)
    return this.unwrapResponse<MealContributor>(response)
  }

  // Update meal contributor
  async updateMealContributor(mealId: string, updateData: UpdateMealContributorRequest): Promise<MealContributor> {
    const response = await this.apiClient.patch(`/meals/${mealId}/contributors`, updateData)
    return this.unwrapResponse<MealContributor>(response)
  }

  // Remove meal contributor
  async removeMealContributor(mealId: string): Promise<void> {
    await this.apiClient.delete(`/meals/${mealId}/contributors`)
  }

  // Opt out of meal
  async optOutOfMeal(mealId: string): Promise<void> {
    await this.apiClient.post(`/meals/${mealId}/opt-out`, {})
  }

  // Opt back in to meal
  async optInToMeal(mealId: string): Promise<void> {
    await this.apiClient.delete(`/meals/${mealId}/opt-out`)
  }
}
